/**
 * Файловый сервис (E15 / ФТ-8.1, НФТ-16).
 * Лимит 100 МБ; исполняемые расширения отклоняются; MIME/расширение сверяются с типом.
 */

import { z } from "zod";

/** Максимальный размер загружаемого файла (ФТ-8.1). */
export const FILE_MAX_BYTES = 100 * 1024 * 1024;

export const fileTypeSchema = z.enum([
  "image",
  "reference",
  "text",
  "methodology_source",
  "bom",
  "price",
  "media_3d",
  "matrix",
]);
export type FileType = z.infer<typeof fileTypeSchema>;

/** Типы, доступные публичному `GET /files/{id}` (экскурсия / контент модели). */
export const PUBLIC_FILE_TYPES = ["image", "reference", "text", "media_3d"] as const;
export type PublicFileType = (typeof PUBLIC_FILE_TYPES)[number];

export function isPublicFileType(type: string): type is PublicFileType {
  return (PUBLIC_FILE_TYPES as readonly string[]).includes(type);
}

export const FILE_ERROR_CODES = {
  NOT_FOUND: "FILE_NOT_FOUND",
  NOT_PUBLIC: "FILE_NOT_PUBLIC",
  STORAGE_MISSING: "FILE_STORAGE_MISSING",
  TOO_LARGE: "FILE_TOO_LARGE",
  FORBIDDEN_EXTENSION: "FILE_FORBIDDEN_EXTENSION",
  TYPE_MISMATCH: "FILE_TYPE_MISMATCH",
} as const;
export type FileErrorCode = (typeof FILE_ERROR_CODES)[keyof typeof FILE_ERROR_CODES];

const BLOCKED_EXTENSIONS = new Set([
  ".exe",
  ".bat",
  ".cmd",
  ".com",
  ".msi",
  ".scr",
  ".dll",
  ".so",
  ".dylib",
  ".js",
  ".mjs",
  ".cjs",
  ".vbs",
  ".ps1",
  ".sh",
  ".bash",
  ".php",
  ".jar",
]);

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type TypeRule = {
  extensions: readonly string[];
  mimes: readonly string[];
};

const RULES: Record<FileType, TypeRule> = {
  image: {
    extensions: [".jpg", ".jpeg", ".png", ".webp", ".gif"],
    mimes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  },
  media_3d: {
    extensions: [".glb", ".gltf"],
    mimes: ["model/gltf-binary", "model/gltf+json", "application/octet-stream"],
  },
  reference: {
    extensions: [".pdf", ".jpg", ".jpeg", ".png", ".webp"],
    mimes: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
  },
  text: {
    extensions: [".txt", ".md"],
    mimes: ["text/plain", "text/markdown"],
  },
  bom: {
    extensions: [".xlsx"],
    mimes: [XLSX_MIME, "application/octet-stream"],
  },
  matrix: {
    extensions: [".xlsx"],
    mimes: [XLSX_MIME, "application/octet-stream"],
  },
  methodology_source: {
    extensions: [".xlsx"],
    mimes: [XLSX_MIME, "application/octet-stream"],
  },
  price: {
    extensions: [".xlsx"],
    mimes: [XLSX_MIME, "application/octet-stream"],
  },
};

export type UploadValidationInput = {
  originalName: string;
  mimeType: string | undefined;
  sizeBytes: number;
  type: FileType;
};

export type UploadValidationResult =
  | { ok: true }
  | { ok: false; code: FileErrorCode; message: string };

/**
 * Проверка загрузки до записи в MinIO (ФТ-8.1).
 * Расширение обязательно; MIME — если передан клиентом.
 */
export function validateUpload(input: UploadValidationInput): UploadValidationResult {
  if (!Number.isFinite(input.sizeBytes) || input.sizeBytes < 0) {
    return {
      ok: false,
      code: FILE_ERROR_CODES.TYPE_MISMATCH,
      message: "Некорректный размер файла",
    };
  }
  if (input.sizeBytes > FILE_MAX_BYTES) {
    return {
      ok: false,
      code: FILE_ERROR_CODES.TOO_LARGE,
      message: "Размер файла превышает 100 МБ",
    };
  }

  const extension = fileExtension(input.originalName);
  if (extension === null || BLOCKED_EXTENSIONS.has(extension)) {
    return {
      ok: false,
      code: FILE_ERROR_CODES.FORBIDDEN_EXTENSION,
      message: "Исполняемые и скриптовые файлы запрещены",
    };
  }

  const rule = RULES[input.type];
  if (!rule.extensions.includes(extension)) {
    return {
      ok: false,
      code: FILE_ERROR_CODES.TYPE_MISMATCH,
      message: `Расширение ${extension} не подходит для типа ${input.type}`,
    };
  }

  const mime = input.mimeType?.trim().toLowerCase();
  if (mime && mime.length > 0 && !rule.mimes.includes(mime)) {
    return {
      ok: false,
      code: FILE_ERROR_CODES.TYPE_MISMATCH,
      message: `MIME ${mime} не подходит для типа ${input.type}`,
    };
  }

  return { ok: true };
}

export function fileExtension(originalName: string): string | null {
  const trimmed = originalName.trim();
  const dot = trimmed.lastIndexOf(".");
  if (dot <= 0 || dot === trimmed.length - 1) {
    return null;
  }
  return trimmed.slice(dot).toLowerCase();
}

/** Путь публичной выдачи файла относительно `/api/v1`. */
export function publicFilePath(fileId: string): string {
  return `/files/${fileId}`;
}
